// Resultado da busca. Mesma linha da lista, sem virtualização: o servidor
// devolve uma página, e rolar resultado de busca não é o mesmo gesto que
// rolar uma caixa de entrada com 40 mil mensagens.

import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import * as correio from "../api/correio";
import { explicar } from "../api/problema";
import { Icone } from "../ui/Icone";
import { corDoAvatar, dataCurta, inicial, nomeCurto } from "../ui/formato";

export function Busca() {
  const [parametros] = useSearchParams();
  const termo = parametros.get("q") ?? "";

  const consulta = useQuery({
    queryKey: ["busca", termo],
    queryFn: ({ signal }) => correio.buscar(termo, undefined, signal),
    enabled: termo.trim().length > 0,
  });

  const itens = consulta.data?.itens ?? [];

  return (
    <div className="flex-1 flex flex-col bg-white rounded-tl-2xl overflow-hidden min-w-0">
      <div className="px-4 py-3 border-b border-linha-suave text-sm text-tinta-media">
        {consulta.isPending && termo ? (
          "buscando…"
        ) : (
          <>
            <strong className="text-tinta">{itens.length}</strong> resultado(s) para “{termo}”
          </>
        )}
      </div>

      {consulta.isError && (
        <p className="p-6 text-sm text-perigo">{explicar(consulta.error)}</p>
      )}

      {!consulta.isPending && itens.length === 0 && termo && (
        <div className="flex-1 grid place-items-center px-6 text-center text-sm text-tinta-fraca">
          {/* O QUE A BUSCA ALCANÇA, DITO SEM ARREDONDAR.
              Desde a migração 52 ela olha também o INÍCIO do corpo (o
              `preview`, uns 200 caracteres). Palavra no meio de uma mensagem
              longa continua fora, e dizer só "cobre o corpo" faria a pessoa
              concluir que a mensagem não existe mais — que é a conclusão
              mais cara possível numa caixa de e-mail.
              Medido em 31/10/2026: procurar no corpo completo pelo Dovecot
              levou 121 segundos numa caixa de 32.687 mensagens. */}
          <p className="max-w-md">
            Nada encontrado. A busca cobre <strong>assunto</strong>,{" "}
            <strong>remetente</strong> e o <strong>início do corpo</strong> da
            mensagem — palavra que apareça só no meio de um texto longo não é
            encontrada aqui.
          </p>
        </div>
      )}

      <div className="overflow-y-auto">
        {itens.map((m) => (
          <Link
            key={m.uuid}
            to={`/correio/${encodeURIComponent(m.pasta ?? "INBOX")}/${m.uuid}`}
            className="flex items-center gap-3 px-3 py-2 text-sm border-b border-linha-suave hover:bg-fundo"
          >
            <span
              className="h-6 w-6 rounded-full grid place-items-center text-white text-[11px] font-semibold flex-shrink-0"
              style={{ background: corDoAvatar(m.remetente) }}
              aria-hidden="true"
            >
              {inicial(m.remetente_nome, m.remetente)}
            </span>
            <span className="w-44 truncate flex-shrink-0">
              {nomeCurto(m.remetente_nome, m.remetente)}
            </span>
            <span className="flex-1 truncate min-w-0">
              {/* A pasta aparece porque a busca cruza pastas: sem isso, o
                  usuário abre um resultado e não entende por que ele não
                  estava na caixa de entrada. */}
              <span className="mr-2 rounded bg-linha px-1.5 py-0.5 text-xs text-tinta-media font-normal">
                {m.pasta_rotulo || m.pasta}
              </span>
              {m.assunto || "(sem assunto)"}
              <span className="font-normal text-tinta-fraca"> – {m.preview}</span>
            </span>
            {m.tem_anexo && <Icone nome="Paperclip" tamanho={14} className="text-tinta-fraca" />}
            <span className="w-16 text-right text-xs tabular-nums flex-shrink-0">
              {dataCurta(m.data)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
